// ============================================================
// TanStack / React Query Hooks for Expenses & Categories
// ============================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expenseService } from '@/services/expenseService';
import { useSchool } from './useSchool';

/**
 * Fetch all expense categories
 */
export function useExpenseCategories(includeInactive = true) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['expenseCategories', schoolId, includeInactive],
    queryFn: () => expenseService.getCategories(schoolId, includeInactive),
    enabled: !!schoolId,
  });
}

/**
 * Create expense category mutation
 */
export function useCreateExpenseCategory() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => expenseService.createCategory(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseCategories', schoolId] });
    },
  });
}

/**
 * Update expense category mutation
 */
export function useUpdateExpenseCategory() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => expenseService.updateCategory(schoolId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseCategories', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['expenses', schoolId] });
    },
  });
}

/**
 * Delete expense category mutation
 */
export function useDeleteExpenseCategory() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => expenseService.deleteCategory(schoolId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseCategories', schoolId] });
    },
  });
}

/**
 * Fetch expenses list with optional filters
 */
export function useExpenses(filters = {}) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['expenses', schoolId, filters],
    queryFn: () => expenseService.getExpenses(schoolId, filters),
    enabled: !!schoolId,
  });
}

/**
 * Fetch single expense by ID
 */
export function useExpense(id) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['expense', schoolId, id],
    queryFn: () => expenseService.getExpenseById(schoolId, id),
    enabled: !!schoolId && !!id,
  });
}

/**
 * Create expense mutation
 */
export function useCreateExpense() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => expenseService.createExpense(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummaryTotal', schoolId] });
    },
  });
}

/**
 * Update expense mutation
 */
export function useUpdateExpense() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => expenseService.updateExpense(schoolId, id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['expense', schoolId, variables.id] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummaryTotal', schoolId] });
    },
  });
}

/**
 * Delete expense mutation
 */
export function useDeleteExpense() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => expenseService.deleteExpense(schoolId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummaryTotal', schoolId] });
    },
  });
}

/**
 * Fetch total expense summary
 */
export function useExpenseSummaryTotal(filters = {}) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['expenseSummaryTotal', schoolId, filters],
    queryFn: () => expenseService.getSummaryTotal(schoolId, filters),
    enabled: !!schoolId,
  });
}
